/**
 * Per-enemy shot recipes — bullets always originate at the enemy's position.
 * Called only while the enemy is alive and on-screen.
 */

export type BulletSpawn = {
  x: number;
  y: number;
  vx: number;
  vy: number;
  r: number;
};

export type FireContext = {
  enemyX: number;
  enemyY: number;
  shipX: number;
  shipY: number;
  kind: "drone" | "glyph" | "warden";
  intensity: number;
  phase: number;
};

export function fireFromEnemy(ctx: FireContext): BulletSpawn[] {
  const { enemyX, enemyY, shipX, shipY, kind, intensity } = ctx;
  const out: BulletSpawn[] = [];
  const baseSpd = 140 + intensity * 60;

  if (kind === "drone") {
    // Straight down from ship
    out.push({
      x: enemyX,
      y: enemyY + 12,
      vx: 0,
      vy: baseSpd * 0.95,
      r: 4,
    });
  } else if (kind === "glyph") {
    // Aimed at player + slight spread (still from enemy)
    const dx = shipX - enemyX;
    const dy = shipY - enemyY;
    const len = Math.hypot(dx, dy) || 1;
    const spd = baseSpd * 0.9;
    out.push({
      x: enemyX,
      y: enemyY + 10,
      vx: (dx / len) * spd,
      vy: (dy / len) * spd,
      r: 4,
    });
    // Two side shots from same origin
    const ang = Math.atan2(dy, dx);
    for (const a of [ang - 0.35, ang + 0.35]) {
      out.push({
        x: enemyX,
        y: enemyY + 10,
        vx: Math.cos(a) * spd * 0.85,
        vy: Math.sin(a) * spd * 0.85,
        r: 3.5,
      });
    }
  } else {
    // Warden: ring from enemy center + one aimed
    const n = 8;
    const spd = baseSpd * 0.75;
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2 + ctx.phase;
      out.push({
        x: enemyX,
        y: enemyY,
        vx: Math.cos(a) * spd,
        vy: Math.sin(a) * spd,
        r: 4,
      });
    }
    const dx = shipX - enemyX;
    const dy = shipY - enemyY;
    const len = Math.hypot(dx, dy) || 1;
    out.push({
      x: enemyX,
      y: enemyY + 8,
      vx: (dx / len) * baseSpd,
      vy: (dy / len) * baseSpd,
      r: 4.5,
    });
  }

  return out;
}
